import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { usePermissions } from '../auth/store';
import { ForbiddenPage } from '../pages/errors/ForbiddenPage';

/**
 * Route guard. Renders the route only when the admin holds `permission`,
 * otherwise a 403 page — so a URL typed by hand is refused exactly like a
 * hidden menu item.
 *
 * This is a UX guard, not a security boundary: the APIs behind every page
 * check the same permission server-side (AdminAuthGuard), so bypassing this
 * component gets an empty page full of 403s rather than data.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: string;
  children?: ReactNode;
}) {
  const { can } = usePermissions();
  if (!can(permission)) return <ForbiddenPage permission={permission} />;
  return <>{children ?? <Outlet />}</>;
}

/**
 * Renders `children` only when the admin holds the permission — for action
 * buttons (Create / Edit / Delete / Refund / Approve …). Being able to open a
 * page never implies being able to act on it, so every mutating control is
 * wrapped in one of these.
 *
 * Pass `fallback` to show something in its place (e.g. a disabled button with
 * a tooltip) instead of hiding it.
 */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = usePermissions();
  return <>{can(permission) ? children : fallback}</>;
}
